import React from "react";
import {useTranslation} from "react-i18next";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {Editor} from "primereact/editor";
import {FileUpload} from "primereact/fileupload";
import {InputSwitch} from "primereact/inputswitch";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {AppContext} from "../../../HomePage";
import {EmailCampaignService} from "../../../service/EmailCampaignService";
import RecipientBuilder from "./RecipientBuilder";

// Client-side recipient parsing mirrors the backend parse_recipients: split on
// comma/semicolon/newline, trim, lowercase, dedupe; the backend re-validates
// authoritatively on send.
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// Sender addresses must be on the PriceRest domain (verified in SendGrid);
// the backend enforces this too.
const FROM_EMAIL_DOMAIN = '@pricerest.com';
const DEFAULT_FROM_EMAIL = 'noreply@pricerest.com';

function isValidFromEmail(value) {
    const candidate = (value || '').trim().toLowerCase();
    return EMAIL_RE.test(candidate) && candidate.endsWith(FROM_EMAIL_DOMAIN);
}

function parseRecipients(raw) {
    const valid = [];
    const invalid = [];
    const seen = new Set();
    (raw || '').split(/[,;\n]/).forEach((token) => {
        const stripped = token.trim();
        if (!stripped) return;
        if (EMAIL_RE.test(stripped)) {
            const lowered = stripped.toLowerCase();
            if (!seen.has(lowered)) {
                seen.add(lowered);
                valid.push(lowered);
            }
        } else {
            invalid.push(stripped);
        }
    });
    return {valid, invalid};
}

export default function EmailCampaignCompose() {
    const {t} = useTranslation();
    const {toast, setError} = React.useContext(AppContext);
    const service = React.useMemo(() => new EmailCampaignService(), []);

    const [recipientsRaw, setRecipientsRaw] = React.useState('');
    const [criteria, setCriteria] = React.useState(null);
    const [fromName, setFromName] = React.useState('PriceRest.com');
    const [fromEmail, setFromEmail] = React.useState(DEFAULT_FROM_EMAIL);
    const [subject, setSubject] = React.useState('');
    const [htmlBody, setHtmlBody] = React.useState('');
    const [htmlMode, setHtmlMode] = React.useState(false);
    const [attachments, setAttachments] = React.useState([]); // [{file_id,name,type}]
    const [optOutFooter, setOptOutFooter] = React.useState(true);
    const [previewVisible, setPreviewVisible] = React.useState(false);
    const [testEmail, setTestEmail] = React.useState('');
    const [sending, setSending] = React.useState(false);
    const [confirmVisible, setConfirmVisible] = React.useState(false);

    const parsed = React.useMemo(() => parseRecipients(recipientsRaw), [recipientsRaw]);
    const fromEmailValid = isValidFromEmail(fromEmail);

    const uploadAttachment = ({files}) => {
        const file = files[0];
        service.uploadAttachment(file)
            .then((ref) => {
                setAttachments((prev) => [...prev, ref]);
                toast.current.show({severity: 'success', summary: ref.name});
            })
            .catch((e) => {
                setError(e);
                setError(null);
            });
    };

    const removeAttachment = (fileId) => {
        setAttachments((prev) => prev.filter((a) => a.file_id !== fileId));
    };

    const previewHtml = () => htmlBody + (optOutFooter
        ? '<hr/><p style="font-size:12px;color:#888888;">You are receiving this email from PriceRest. If you no longer wish to receive these emails, reply to this message with "unsubscribe".</p>'
        : '');

    const doSendTest = () => {
        if (!testEmail.trim() || !fromEmailValid) return;
        service.sendTest({
            subject, html_body: htmlBody, attachments,
            opt_out_footer: optOutFooter, test_email: testEmail.trim(),
            from_name: fromName.trim(), from_email: fromEmail.trim(),
        }).then(() => {
            toast.current.show({severity: 'success', summary: t('email_campaign.test_sent')});
        }).catch((e) => {
            setError(e);
            setError(null);
        });
    };

    const doSend = () => {
        setConfirmVisible(false);
        setSending(true);
        service.send({
            subject, html_body: htmlBody, recipients_raw: recipientsRaw,
            attachments, opt_out_footer: optOutFooter,
            from_name: fromName.trim(), from_email: fromEmail.trim(),
            criteria,
        }).then(() => {
            toast.current.show({severity: 'success', summary: t('email_campaign.sent_queued')});
            setRecipientsRaw('');
            setSubject('');
            setHtmlBody('');
            setAttachments([]);
        }).catch((e) => {
            setError(e);
            setError(null);
        }).finally(() => setSending(false));
    };

    const canSend = parsed.valid.length > 0 && subject.trim().length > 0 && fromEmailValid && !sending;

    return (
        <div className="p-fluid">
            <RecipientBuilder onChange={({recipientsRaw: raw, criteria: crit}) => {
                setRecipientsRaw(raw);
                setCriteria(crit);
            }}/>

            <div className="p-grid">
                <div className="p-field p-col-12 p-md-6">
                    <label>{t('email_campaign.from_name')}</label>
                    <InputText value={fromName}
                               placeholder={t('email_campaign.from_name_hint')}
                               onChange={(e) => setFromName(e.target.value)}/>
                </div>
                <div className="p-field p-col-12 p-md-6">
                    <label>{t('email_campaign.from_email')}</label>
                    <InputText value={fromEmail} className={fromEmailValid ? '' : 'p-invalid'}
                               onChange={(e) => setFromEmail(e.target.value)}/>
                    {!fromEmailValid &&
                        <small className="p-error">{t('email_campaign.from_email_invalid')}</small>}
                </div>
            </div>

            <div className="p-field">
                <label>{t('email_campaign.subject')}</label>
                <InputText value={subject} onChange={(e) => setSubject(e.target.value)}/>
            </div>

            <div className="p-field">
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                             gap: '1em', flexWrap: 'wrap', marginBottom: '.25em'}}>
                    <label style={{margin: 0}}>{t('email_campaign.body')}</label>
                    <div style={{display: 'flex', alignItems: 'center', gap: '1.5em', flexWrap: 'wrap'}}>
                        <FileUpload mode="basic" customUpload auto chooseLabel={t('email_campaign.attachments')}
                                    chooseOptions={{className: 'p-button-outlined p-button-sm'}}
                                    uploadHandler={uploadAttachment}/>
                        <span style={{display: 'inline-flex', alignItems: 'center', gap: '.5em'}}>
                            <InputSwitch checked={optOutFooter} onChange={(e) => setOptOutFooter(e.value)}/>
                            <label style={{margin: 0}}>{t('email_campaign.opt_out_footer')}</label>
                        </span>
                        <span style={{display: 'inline-flex', alignItems: 'center', gap: '.5em'}}>
                            <label style={{margin: 0}}>{t('email_campaign.html_source')}</label>
                            <InputSwitch checked={htmlMode} onChange={(e) => setHtmlMode(e.value)}/>
                        </span>
                    </div>
                </div>
                {htmlMode
                    ? <InputTextarea rows={14} value={htmlBody} style={{fontFamily: 'monospace'}}
                                     placeholder={t('email_campaign.html_source_hint')}
                                     onChange={(e) => setHtmlBody(e.target.value)}/>
                    : <Editor style={{height: '320px'}} value={htmlBody}
                              onTextChange={(e) => setHtmlBody(e.htmlValue || '')}/>}
                {attachments.length > 0 &&
                    <ul style={{marginTop: '.5em', paddingLeft: '1.2em'}}>
                        {attachments.map((a) => (
                            <li key={a.file_id}>
                                {a.name}{' '}
                                <Button icon="pi pi-times" className="p-button-text p-button-danger p-button-sm"
                                        onClick={() => removeAttachment(a.file_id)}/>
                            </li>
                        ))}
                    </ul>}
            </div>

            <div style={{display: 'flex', gap: '.5em', alignItems: 'center', flexWrap: 'wrap',
                         marginTop: '1em', paddingTop: '1em', borderTop: '1px solid #e9ecef'}}>
                <Button label={t('email_campaign.preview')} icon="pi pi-eye"
                        className="p-button-outlined p-button-secondary" style={{width: 'auto'}}
                        onClick={() => setPreviewVisible(true)}/>
                <InputText style={{width: '220px', marginLeft: 'auto'}} value={testEmail}
                           placeholder={t('email_campaign.test_email')}
                           onChange={(e) => setTestEmail(e.target.value)}/>
                <Button label={t('email_campaign.send_test')} icon="pi pi-send"
                        className="p-button-outlined" style={{width: 'auto'}}
                        disabled={!fromEmailValid || !testEmail.trim()} onClick={doSendTest}/>
                <Button label={t('email_campaign.send')} icon="pi pi-check"
                        className="p-button-success" style={{width: 'auto'}}
                        disabled={!canSend} loading={sending}
                        onClick={() => setConfirmVisible(true)}/>
            </div>

            <Dialog header={t('email_campaign.preview')} visible={previewVisible}
                    style={{width: '640px'}} onHide={() => setPreviewVisible(false)}>
                <div dangerouslySetInnerHTML={{__html: previewHtml()}}/>
            </Dialog>

            <Dialog header={t('email_campaign.send')} visible={confirmVisible}
                    style={{width: '420px'}} onHide={() => setConfirmVisible(false)}
                    footer={
                        <React.Fragment>
                            <Button label={t('email_campaign.cancel')} icon="pi pi-times"
                                    className="p-button-text p-button-secondary" onClick={() => setConfirmVisible(false)}/>
                            <Button label={t('email_campaign.send')} icon="pi pi-check"
                                    className="p-button-success" onClick={doSend}/>
                        </React.Fragment>
                    }>
                <span>{t('email_campaign.send_confirm', {count: parsed.valid.length})}</span>
            </Dialog>
        </div>
    );
}
